/**
 * DevTools — State Machine Debugger (exhaustif, 4 pipelines)
 * ─────────────────────────────────────────────────────────────────────────────
 * Trace EXHAUSTIVE des 4 pipelines en chaîne qui résolvent l'action d'une tâche :
 *
 *   PIPELINE 1 — getTaskAction(task, payments)
 *       → action "soft" : { type: 'view-form' | 'view' | 'pay' | 'proceed' ... }
 *       Source: features/workspace/admin/pages/demarches-page/demarches-page.model.ts
 *
 *   PIPELINE 2 — resolveTaskAction(event, demarche, tasks, payments)
 *       → TaskActionResult concret (navigate-payment-context, proceed-task, ...)
 *       Source: features/.../demarches-page/demarches-task-actions.handler.ts
 *
 *   PIPELINE 3 — validateTaskAction(result, demarche, tasks, payments)
 *       → { valid, reason? } (checks serviceId, montant, terminal statuses, receipt)
 *       Source: même handler.
 *
 *   PIPELINE 4 — Dispatch + side effects (incluant SOUMISSION SILENCIEUSE)
 *       → proceedWithTask → getFormView → decompress → buildBody → submitFormView
 *       Source: demarches-page.component.ts + services/demarches-form.service.ts
 *
 * Approche : duplication volontaire et tracée des 4 pipelines. Aucun side-effect
 * réel — simulation/prédiction exclusivement.
 */
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { DevToolsService } from '../dev-tools.service';
import { safeStringify } from '../dev-tools.types';
import { TaskRecordDto } from '../../models/guest/requests.dto';
import {
  Demarche,
  Payment,
  SyntheticPaymentMeta,
} from '../../../features/workspace/admin/pages/demarches-page/demarches-page.model';
import { TaskActionResult } from '../../../features/workspace/admin/pages/demarches-page/demarches-task-actions.handler';
import { PaymentContextData } from '../../models/guest/payment-websocket.dto';
import {
  FieldHighlightConfig,
  HighlightPriority,
  HighlightType,
} from '../../models/guest/field-highlight.interface';

// ============================================================================
// Constantes dupliquées (source de vérité visible)
// ============================================================================

enum BpmnType {
  START_EVENT = 'bpmn:StartEvent',
  END_EVENT = 'bpmn:EndEvent',
  USER_TASK = 'bpmn:UserTask',
  SERVICE_TASK = 'bpmn:ServiceTask',
  MANUAL_TASK = 'bpmn:ManualTask',
}

enum HandlerType {
  TARIFICATION = 'tarification',
  GENERATE_BORDEREAU = 'generate_bordereau',
  PUBLISH_SUBMISSION = 'publish_submission',
  DOWNLOAD_FILE = 'download_file',
  DOWNLOAD_FILES = 'download_files',
  APPOINTMENT = 'appointment',
  CERTIFICATION = 'certification',
}

const TERMINAL_STATUSES = ['Completed', 'Success', 'Failed', 'ReqFail', 'cancelled'] as const;

// ============================================================================
// Types internes (UI)
// ============================================================================

interface SoftAction {
  label: string;
  icon: string;
  type: string;
  disabled?: boolean;
}

type StepResult = 'pass' | 'fail' | 'skip';

interface DecisionStep {
  index: number;
  label: string;
  sourceRef: string;
  evaluated: string;
  value: unknown;
  result: StepResult;
  branchTaken?: string;
  details?: string[];
}

type SectionStatus = 'ok' | 'blocked' | 'silent' | 'interactive' | 'noop';

interface PipelineSection {
  id: 'p1' | 'p2' | 'p3' | 'p4';
  title: string;
  subtitle: string;
  status: SectionStatus;
  steps: DecisionStep[];
  summary?: string;
}

type HighlightSource = 'direct' | 'message.reason' | 'aucun';

interface HighlightInfo {
  source: HighlightSource;
  configs: FieldHighlightConfig[];
  globalMessage: string | null;
  isResubmissionContext: boolean;
  willOpenForm: boolean;
}

interface PipelineTrace {
  taskName: string;
  taskId: string;
  status: string;
  handlerType: string;
  bpmnType: string;
  // Pipeline outputs
  softAction: SoftAction | null;
  actionResult: TaskActionResult | null;
  validation: { valid: boolean; reason?: string };
  terminalBehavior: string; // résumé P4
  isSilentSubmission: boolean;
  isInteractiveRedirect: boolean;
  // Highlights de resoumission (extraits par P4 / aperçu task)
  highlights: HighlightInfo;
  // Sections UI
  sections: PipelineSection[];
}

// ----------------------------------------------------------------------------
// Highlight extraction (mirror RequestFormPageComponent)
// ----------------------------------------------------------------------------

interface FormSettingShape {
  status?: string;
  correctionKeys?: unknown;
  message?: {
    text?: string;
    reason?: {
      message?: string;
      text?: string;
      correctionKeys?: unknown;
    };
  };
}

function extractHighlightInfo(task: TaskRecordDto, p4Type: string | null): HighlightInfo {
  const formSetting = (task as unknown as { formSetting?: FormSettingShape }).formSetting;
  const directKeys = formSetting?.correctionKeys;
  const reasonKeys = formSetting?.message?.reason?.correctionKeys;

  let source: HighlightSource = 'aucun';
  let raw: unknown = null;
  if (Array.isArray(directKeys) && directKeys.length > 0) {
    source = 'direct';
    raw = directKeys;
  } else if (Array.isArray(reasonKeys) && reasonKeys.length > 0) {
    source = 'message.reason';
    raw = reasonKeys;
  }

  const configs: FieldHighlightConfig[] = Array.isArray(raw)
    ? (raw as unknown[])
        .filter((it): it is Record<string, unknown> => !!it && typeof it === 'object')
        .map((it) => ({
          fieldKey: typeof it['fieldKey'] === 'string' ? (it['fieldKey'] as string) : '(inconnu)',
          highlightType:
            (typeof it['highlightType'] === 'string'
              ? (it['highlightType'] as HighlightType)
              : 'update-required'),
          message: typeof it['message'] === 'string' ? (it['message'] as string) : undefined,
          priority:
            typeof it['priority'] === 'string'
              ? (it['priority'] as HighlightPriority)
              : undefined,
          autoFocus: typeof it['autoFocus'] === 'boolean' ? (it['autoFocus'] as boolean) : undefined,
        }))
    : [];

  const reason = formSetting?.message?.reason;
  const globalMessage = reason?.message ?? reason?.text ?? formSetting?.message?.text ?? null;

  const status = task.status;
  const isRejected = formSetting?.status === 'rejected' || status === 'PendingResubmit';
  const willOpenForm = p4Type === 'proceed-form-view';
  const isResubmissionContext = isRejected || willOpenForm || configs.length > 0;

  return { source, configs, globalMessage, isResubmissionContext, willOpenForm };
}

// ============================================================================
// PIPELINE 1 — getTaskAction tracé
// ============================================================================

function tracePipeline1(task: TaskRecordDto, payments: Payment[]): {
  action: SoftAction | null;
  steps: DecisionStep[];
} {
  const steps: DecisionStep[] = [];
  const status = task.status;
  const bpmnType = task.bpmnType || '';
  const handlerType = (task.handlerType || task.data?.['handlerType'] || '') as string;
  let action: SoftAction | null = null;

  // [1] synthetic / enrichment
  const syntheticMeta = task.data?.['__syntheticPayment'] as SyntheticPaymentMeta | undefined;
  const enrichmentMeta = task.data?.['__paymentEnrichment'] as SyntheticPaymentMeta | undefined;
  const paymentMeta = syntheticMeta ?? enrichmentMeta;

  if (paymentMeta?.isSyntheticPayment) {
    const paymentStatus = (paymentMeta.paymentStatus || '').toUpperCase();
    let branch = '';
    if (paymentStatus === 'SUCCEEDED' || paymentStatus === 'REFUNDED') {
      action = { label: 'TASK_ACTION.TELECHARGER', icon: 'ph ph-download-simple', type: 'download-receipt' };
      branch = 'getPaymentTaskAction → SUCCEEDED/REFUNDED → download-receipt';
    } else if (paymentStatus === 'FAILED' || paymentStatus === 'EXPIRED' || paymentStatus === 'CANCELLED') {
      action = { label: 'TASK_ACTION.RELANCER', icon: 'ph ph-arrow-counter-clockwise', type: 'retry-payment' };
      branch = 'getPaymentTaskAction → FAILED/EXPIRED/CANCELLED → retry-payment';
    } else if (paymentStatus === 'PROCESSING' || paymentStatus === 'PENDING') {
      action = { label: 'TASK_ACTION.CONTINUER', icon: 'ph ph-arrow-right', type: 'continue-payment' };
      branch = 'getPaymentTaskAction → PROCESSING/PENDING → continue-payment';
    } else {
      action = { label: 'TASK_ACTION.PAYER', icon: 'ph ph-credit-card', type: 'pay' };
      branch = 'getPaymentTaskAction → default → pay';
    }
    steps.push({
      index: 1,
      label: 'Tâche synthétique / enrichie paiement ?',
      sourceRef: 'demarches-page.model.ts:540',
      evaluated: 'task.data.__syntheticPayment || task.data.__paymentEnrichment',
      value: { syntheticPayment: !!syntheticMeta, paymentEnrichment: !!enrichmentMeta, paymentStatus },
      result: 'pass',
      branchTaken: branch,
    });
    steps.push(skip(2, 'bpmnType = START_EVENT ?', 'demarches-page.model.ts:556', 'task.bpmnType'));
    steps.push(skip(3, 'handlerType défini ?', 'demarches-page.model.ts:561', 'task.handlerType'));
    steps.push(skip(4, 'formSetting.status=rejected || PendingResubmit ?', 'demarches-page.model.ts:600', 'task.formSetting.status'));
    steps.push(skip(5, 'Fallback legacy (getLegacyTaskAction)', 'demarches-page.model.ts:607', 'getLegacyTaskAction(task, payments)'));
    return { action, steps };
  }
  steps.push({
    index: 1,
    label: 'Tâche synthétique / enrichie paiement ?',
    sourceRef: 'demarches-page.model.ts:540',
    evaluated: 'task.data.__syntheticPayment || task.data.__paymentEnrichment',
    value: { syntheticPayment: !!syntheticMeta, paymentEnrichment: !!enrichmentMeta },
    result: 'fail',
  });

  // [1c] END_EVENT — la tâche de fin ne porte plus d'action, quels que soient
  // son statut et son handler (garde 1c de getTaskAction).
  if (bpmnType === BpmnType.END_EVENT) {
    steps.push({
      index: 2,
      label: 'bpmnType = END_EVENT ? (tâche de fin : aucune action)',
      sourceRef: 'demarches-page.model.ts:599',
      evaluated: 'task.bpmnType',
      value: bpmnType,
      result: 'pass',
      branchTaken: 'END_EVENT → null (plus de récépissé fabriqué)',
    });
    steps.push(skip(3, 'handlerType défini ?', 'demarches-page.model.ts:561', 'task.handlerType'));
    steps.push(skip(4, 'formSetting.status=rejected || PendingResubmit ?', 'demarches-page.model.ts:600', 'task.formSetting.status'));
    steps.push(skip(5, 'Fallback legacy (getLegacyTaskAction)', 'demarches-page.model.ts:607', 'getLegacyTaskAction(task, payments)'));
    return { action: null, steps };
  }

  // [2] START_EVENT
  if (bpmnType === BpmnType.START_EVENT) {
    action = { label: 'TASK_ACTION.VOIR_LE_FORMULAIRE', icon: 'ph ph-file-text', type: 'view-form' };
    steps.push({
      index: 2,
      label: 'bpmnType = START_EVENT ?',
      sourceRef: 'demarches-page.model.ts:556',
      evaluated: 'task.bpmnType',
      value: bpmnType,
      result: 'pass',
      branchTaken: '→ view-form',
    });
    steps.push(skip(3, 'handlerType défini ?', 'demarches-page.model.ts:561', 'task.handlerType'));
    steps.push(skip(4, 'formSetting.status=rejected || PendingResubmit ?', 'demarches-page.model.ts:600', 'task.formSetting.status'));
    steps.push(skip(5, 'Fallback legacy (getLegacyTaskAction)', 'demarches-page.model.ts:607', 'getLegacyTaskAction(task, payments)'));
    return { action, steps };
  }
  steps.push({
    index: 2,
    label: 'bpmnType = START_EVENT ?',
    sourceRef: 'demarches-page.model.ts:556',
    evaluated: 'task.bpmnType',
    value: bpmnType,
    result: 'fail',
  });

  // [3] handlerType
  if (handlerType) {
    const { action: ha, branch, details } = resolveHandlerAction(handlerType as HandlerType, status, payments);
    action = ha;
    steps.push({
      index: 3,
      label: 'handlerType défini ?',
      sourceRef: 'demarches-page.model.ts:561',
      evaluated: 'task.handlerType || task.data.handlerType',
      value: handlerType,
      result: 'pass',
      branchTaken: branch,
      details,
    });
    steps.push(skip(4, 'formSetting.status=rejected || PendingResubmit ?', 'demarches-page.model.ts:600', 'task.formSetting.status'));
    steps.push(skip(5, 'Fallback legacy (getLegacyTaskAction)', 'demarches-page.model.ts:607', 'getLegacyTaskAction(task, payments)'));
    return { action, steps };
  }
  steps.push({
    index: 3,
    label: 'handlerType défini ?',
    sourceRef: 'demarches-page.model.ts:561',
    evaluated: 'task.handlerType || task.data.handlerType',
    value: handlerType || '(aucun)',
    result: 'fail',
  });

  // [4] formSetting.status = rejected || PendingResubmit
  const formStatus = (task as unknown as { formSetting?: { status?: string } }).formSetting?.status;
  const isRejectedOrResubmit = formStatus === 'rejected' || status === 'PendingResubmit';
  const isActionable =
    status === 'Pending' || status === 'PendingPortal' || status === 'PendingResubmit';
  if (isRejectedOrResubmit && isActionable) {
    action = { label: 'TASK_ACTION.CORRIGER', icon: 'ph ph-pencil-simple', type: 'correct' };
    steps.push({
      index: 4,
      label: 'formSetting.status=rejected || PendingResubmit ?',
      sourceRef: 'demarches-page.model.ts:600',
      evaluated: "task.formSetting?.status === 'rejected' || task.status === 'PendingResubmit'",
      value: { formStatus, taskStatus: status },
      result: 'pass',
      branchTaken: '→ correct',
    });
    steps.push(skip(5, 'Fallback legacy (getLegacyTaskAction)', 'demarches-page.model.ts:607', 'getLegacyTaskAction(task, payments)'));
    return { action, steps };
  }
  steps.push({
    index: 4,
    label: 'formSetting.status=rejected || PendingResubmit ?',
    sourceRef: 'demarches-page.model.ts:600',
    evaluated: "task.formSetting?.status === 'rejected' || task.status === 'PendingResubmit'",
    value: { formStatus, taskStatus: status, isActionable },
    result: 'fail',
  });

  // [5] Legacy
  const legacy = traceLegacy(task, payments);
  action = legacy.action;
  steps.push({
    index: 5,
    label: 'Fallback legacy (getLegacyTaskAction)',
    sourceRef: 'demarches-page.model.ts:633',
    evaluated: 'getLegacyTaskAction(task, payments)',
    value: { name: task.name, bpmnType, status },
    result: legacy.action ? 'pass' : 'fail',
    branchTaken: legacy.branch,
    details: legacy.details,
  });
  return { action, steps };
}

function skip(index: number, label: string, sourceRef: string, evaluated: string): DecisionStep {
  return { index, label, sourceRef, evaluated, value: '(skipped)', result: 'skip' };
}

function resolveHandlerAction(
  handlerType: HandlerType,
  status: string,
  payments: Payment[]
): { action: SoftAction | null; branch: string; details: string[] } {
  switch (handlerType) {
    case HandlerType.TARIFICATION: {
      const hasSucceeded = payments.some(p => p.statusColor === 'success');
      const hasInProgress = payments.some(p => p.statusColor !== 'success' && p.statusColor !== 'error');
      const isCompleted = status === 'Completed' || status === 'Success';
      const details = [
        `payments.length = ${payments.length}`,
        `hasSucceeded (statusColor='success') = ${hasSucceeded}`,
        `hasInProgress (statusColor != success/error) = ${hasInProgress}`,
        `task.status = '${status}' → isCompleted = ${isCompleted}`,
      ];
      if (hasSucceeded) {
        return { action: { label: 'TASK_ACTION.AFFICHER_LA_FACTURE', icon: 'ph ph-receipt', type: 'view' }, branch: 'getTarificationAction → success → view', details };
      }
      if (hasInProgress) {
        return { action: { label: 'TASK_ACTION.AFFICHER', icon: 'ph ph-eye', type: 'view-payment' }, branch: 'getTarificationAction → inProgress → view-payment', details };
      }
      if (isCompleted) {
        return { action: { label: 'TASK_ACTION.PROCEDER', icon: 'ph ph-arrow-right', type: 'proceed', disabled: true }, branch: 'getTarificationAction → completed sans paiement → proceed (disabled)', details };
      }
      return { action: { label: 'TASK_ACTION.PROCEDER', icon: 'ph ph-arrow-right', type: 'proceed' }, branch: 'getTarificationAction → default → proceed', details };
    }
    case HandlerType.GENERATE_BORDEREAU:
      if (status === 'Completed' || status === 'Success') {
        return { action: { label: 'TASK_ACTION.TELECHARGER', icon: 'ph ph-download-simple', type: 'download-receipt' }, branch: 'generate_bordereau → completed → download-receipt', details: [`status = ${status}`] };
      }
      return { action: null, branch: 'generate_bordereau → non terminé → null', details: [`status = ${status}`] };
    case HandlerType.DOWNLOAD_FILE:
    case HandlerType.DOWNLOAD_FILES:
      return { action: { label: 'TASK_ACTION.TELECHARGER', icon: 'ph ph-download-simple', type: 'download-files' }, branch: 'download_file(s) → download-files', details: [] };
    case HandlerType.APPOINTMENT:
      if (status === 'Completed' || status === 'Success') {
        return { action: { label: 'TASK_ACTION.PRENDRE_RDV', icon: 'ph ph-calendar', type: 'appointment', disabled: true }, branch: 'appointment → completed → appointment (disabled)', details: [`status = ${status}`] };
      }
      if (status === 'Pending' || status === 'PendingPortal' || status === 'PendingResubmit') {
        return { action: { label: 'TASK_ACTION.PRENDRE_RDV', icon: 'ph ph-calendar', type: 'appointment' }, branch: 'appointment → pending → appointment', details: [`status = ${status}`] };
      }
      return { action: null, branch: 'appointment → null', details: [`status = ${status}`] };
    case HandlerType.PUBLISH_SUBMISSION:
      if (status === 'Completed' || status === 'Success') {
        return { action: { label: 'TASK_ACTION.PROCEDER', icon: 'ph ph-arrow-right', type: 'proceed', disabled: true }, branch: 'publish_submission → completed → proceed (disabled)', details: [`status = ${status}`] };
      }
      if (status === 'PendingResubmit') {
        return { action: { label: 'TASK_ACTION.CORRIGER', icon: 'ph ph-pencil-simple', type: 'correct' }, branch: 'publish_submission → PendingResubmit → correct', details: [`status = ${status}`] };
      }
      if (status === 'Pending' || status === 'PendingPortal' || status === 'Submitted') {
        return { action: { label: 'TASK_ACTION.PROCEDER', icon: 'ph ph-arrow-right', type: 'proceed' }, branch: 'publish_submission → pending → proceed', details: [`status = ${status}`] };
      }
      return { action: null, branch: 'publish_submission → null', details: [`status = ${status}`] };     
    default:
      return { action: null, branch: `handlerType '${handlerType}' non reconnu`, details: [] };
  }
}

function traceLegacy(task: TaskRecordDto, payments: Payment[]): { action: SoftAction | null; branch: string; details: string[] } {
  const bpmnType = task.bpmnType || '';
  const status = task.status;
  const hasSucceededPayment = payments.some(p => p.statusColor === 'success');
  const hasInProgressPayment = payments.some(p => p.statusColor !== 'success' && p.statusColor !== 'error');
  const details = [`bpmnType = '${bpmnType}'`, `status = '${status}'`];

  // Garde-fou état-driven : tâche en attente de paiement sans handlerType.
  if (status === 'PendingPayment') {
    if (hasSucceededPayment) return { action: { label: 'TASK_ACTION.AFFICHER_LA_FACTURE', icon: 'ph ph-receipt', type: 'view' }, branch: 'legacy payment → success → view', details };
    if (hasInProgressPayment) return { action: { label: 'TASK_ACTION.AFFICHER', icon: 'ph ph-eye', type: 'view-payment' }, branch: 'legacy payment → inProgress → view-payment', details };
    return { action: { label: 'TASK_ACTION.PROCEDER', icon: 'ph ph-arrow-right', type: 'proceed' }, branch: 'legacy payment → proceed', details };
  }
  if (bpmnType === BpmnType.USER_TASK || bpmnType === BpmnType.MANUAL_TASK) {
    if (status === 'Pending' || status === 'PendingPortal' || status === 'Submitted')
      return { action: { label: 'TASK_ACTION.PROCEDER', icon: 'ph ph-arrow-right', type: 'proceed' }, branch: 'legacy user/manual → pending → proceed', details };
    if (status === 'Rejected' || status === 'PendingResubmit')
      return { action: { label: 'TASK_ACTION.CORRIGER', icon: 'ph ph-pencil-simple', type: 'correct' }, branch: 'legacy user/manual → rejected → correct', details };
    if (status === 'Completed' || status === 'Archived')
      return { action: { label: 'TASK_ACTION.AFFICHER', icon: 'ph ph-eye', type: 'view-form' }, branch: 'legacy user/manual → completed → view-form', details };
  }
  if (bpmnType === BpmnType.END_EVENT) {
    // Inatteignable depuis la garde [1c] ; conservé pour qu'une trace directe
    // de ce repli dise la même chose que le modèle.
    return { action: null, branch: 'END_EVENT → tâche de fin : aucune action', details };
  }
  if (bpmnType === BpmnType.SERVICE_TASK && (status === 'Completed' || status === 'Success' || status === 'Archived')) {
    return { action: { label: 'TASK_ACTION.AFFICHER', icon: 'ph ph-eye', type: 'view-form' }, branch: 'legacy SERVICE_TASK → completed → view-form', details };
  }
  return { action: null, branch: 'legacy → aucune règle → null', details };
}

// ============================================================================
// PIPELINE 2 — resolveTaskAction tracé
// ============================================================================

function tracePipeline2(
  task: TaskRecordDto,
  softAction: SoftAction | null,
  demarche: Demarche | null,
  tasks: TaskRecordDto[]
): { result: TaskActionResult | null; steps: DecisionStep[] } {
  const steps: DecisionStep[] = [];
  if (!softAction) {
    steps.push({
      index: 1,
      label: 'Action soft disponible ?',
      sourceRef: 'demarches-task-actions.handler.ts:20',
      evaluated: 'P1 output',
      value: null,
      result: 'fail',
      branchTaken: '→ aucune action à résoudre',
    });
    return { result: null, steps };
  }

  const syntheticMeta = task.data?.['__syntheticPayment'] as SyntheticPaymentMeta | undefined;
  const enrichmentMeta = task.data?.['__paymentEnrichment'] as SyntheticPaymentMeta | undefined;
  const paymentMeta = enrichmentMeta ?? syntheticMeta;
  const actionType = softAction.type;

  steps.push({
    index: 1,
    label: 'Lecture paymentMeta (enrichment ?? synthetic)',
    sourceRef: 'demarches-task-actions.handler.ts:26-28',
    evaluated: 'task.data.__paymentEnrichment ?? task.data.__syntheticPayment',
    value: {
      hasEnrichment: !!enrichmentMeta,
      hasSynthetic: !!syntheticMeta,
      paymentTransactionId: paymentMeta?.paymentTransactionId,
      parentTaskId: syntheticMeta?.parentTaskId,
    },
    result: paymentMeta ? 'pass' : 'fail',
  });

  steps.push({
    index: 2,
    label: `switch(actionType) — entrée dans le case '${actionType}'`,
    sourceRef: 'demarches-task-actions.handler.ts:30',
    evaluated: 'event.actionType',
    value: actionType,
    result: 'pass',
  });

  let result: TaskActionResult | null = null;
  const d: Demarche | null = demarche;

  switch (actionType) {
    case 'view-form':
      result = { type: 'view-form', task };
      steps.push(leaf(3, "case 'view-form' → TaskActionResult.view-form", 'handler.ts:31-32', result));
      break;

    case 'view-invoice':
      if (paymentMeta?.paymentTransactionId) {
        result = { type: 'view-invoice', transactionId: paymentMeta.paymentTransactionId };
        steps.push(leaf(3, "case 'view-invoice' → paymentTransactionId présent → view-invoice", 'handler.ts:35-37', result));
      } else {
        result = { type: 'download-receipt', task };
        steps.push(leaf(3, "case 'view-invoice' → pas de transactionId → fallback download-receipt", 'handler.ts:38', result));
      }
      break;

    case 'view':
      result = { type: 'navigate-payment', task };
      steps.push(leaf(3, "case 'view' → navigate-payment", 'handler.ts:40-41', result));
      break;

    case 'view-payment':
      result = { type: 'view-payment-status', task };
      steps.push(leaf(3, "case 'view-payment' → view-payment-status", 'handler.ts:43-44', result));
      break;

    case 'continue-payment':
      if (paymentMeta && d) {
        const paymentContext = buildContinuePaymentContext(paymentMeta, d);
        const recordId = d.reference || paymentMeta.paymentTransactionId || paymentMeta.paymentId;
        result = {
          type: 'navigate-payment-context',
          paymentContext,
          serviceId: d.serviceUid || d.serviceId,
          recordId,
          retryPaymentId: paymentMeta.paymentId,
        };
        steps.push(leaf(
          3,
          "case 'continue-payment' → paymentMeta présent → navigate-payment-context",
          'handler.ts:46-62',
          result,
          ['paymentContext construit depuis paymentMeta + demarche', `recordId = ${recordId}`]
        ));
      } else {
        result = { type: 'navigate-payment', task };
        steps.push(leaf(3, "case 'continue-payment' → paymentMeta absent → navigate-payment", 'handler.ts:63', result));
      }
      break;

    case 'pay':
    case 'retry-payment': {
      const detailsBase = [
        `hasEnrichmentMeta = ${!!enrichmentMeta}`,
        `hasSyntheticMeta = ${!!syntheticMeta}`,
        `enrichmentMeta.isSyntheticPayment = ${enrichmentMeta?.isSyntheticPayment}`,
        `enrichmentMeta.originalTaskStatus = ${enrichmentMeta?.originalTaskStatus}`,
        `syntheticMeta.parentTaskId = ${syntheticMeta?.parentTaskId}`,
      ];
      if (enrichmentMeta?.isSyntheticPayment && d) {
        const originalStatus = enrichmentMeta.originalTaskStatus;
        if (originalStatus === 'Completed' || originalStatus === 'Success') {
          const ctx = buildPayContext(enrichmentMeta, d);
          result = {
            type: 'navigate-payment-context',
            paymentContext: ctx,
            serviceId: d.serviceUid || d.serviceId,
            retryPaymentId: enrichmentMeta.paymentId || undefined,
          };
          steps.push(leaf(
            3,
            "pay/retry-payment → enrichment + originalStatus=Completed/Success → navigate-payment-context (retry)",
            'handler.ts:78-94',
            result,
            detailsBase
          ));
        } else {
          const restoredTask: TaskRecordDto = { ...task, status: originalStatus || task.status };
          result = { type: 'proceed-task', task: restoredTask };
          steps.push(leaf(
            3,
            "pay/retry-payment → enrichment + originalStatus non-terminal → proceed-task (task restaurée)",
            'handler.ts:95-99',
            result,
            [...detailsBase, `task.status restauré à '${originalStatus || task.status}'`]
          ));
        }
      } else if (syntheticMeta?.parentTaskId) {
        const parentTask = tasks.find(t => t._id === syntheticMeta.parentTaskId);
        if (parentTask && d) {
          if (parentTask.status === 'Completed' || parentTask.status === 'Success') {
            const ctx = buildPayContext(syntheticMeta, d);
            result = {
              type: 'navigate-payment-context',
              paymentContext: ctx,
              serviceId: d.serviceUid || d.serviceId,
              retryPaymentId: syntheticMeta.paymentId || undefined,
            };
            steps.push(leaf(
              3,
              "pay/retry-payment → synthetic + parent Completed → navigate-payment-context",
              'handler.ts:101-117',
              result,
              [...detailsBase, `parentTask.status = '${parentTask.status}'`]
            ));
          } else {
            result = { type: 'proceed-parent-task', parentTask };
            steps.push(leaf(
              3,
              "pay/retry-payment → synthetic + parent non-Completed → proceed-parent-task",
              'handler.ts:119',
              result,
              [...detailsBase, `parentTask.status = '${parentTask.status}'`]
            ));
          }
        } else {
          result = { type: 'noop' };
          steps.push(leaf(3, 'pay/retry-payment → parentTaskId introuvable dans tasks[] → noop', 'handler.ts:122', result, detailsBase));
        }
      } else {
        result = { type: 'noop' };
        steps.push(leaf(3, 'pay/retry-payment → aucun meta exploitable → noop', 'handler.ts:122', result, detailsBase));
      }
      break;
    }

    case 'correct':
    case 'proceed': {
      const resolvedHandler = task.handlerType || (task.data?.['handlerType'] as string | undefined);
      if (resolvedHandler === 'publish_submission') {
        result = { type: 'proceed-form-view', task };
        steps.push(leaf(
          3,
          "correct/proceed → handlerType='publish_submission' → proceed-form-view (mode INTERACTIF)",
          'handler.ts:124-128',
          result,
          [`resolvedHandler = '${resolvedHandler}'`]
        ));
      } else {
        result = { type: 'proceed-task', task };
        steps.push(leaf(
          3,
          "correct/proceed → handlerType autre → proceed-task (SOUMISSION SILENCIEUSE)",
          'handler.ts:130',
          result,
          [`resolvedHandler = '${resolvedHandler ?? '(aucun)'}'`]
        ));
      }
      break;
    }

    case 'download-receipt':
      result = { type: 'download-receipt', task };
      steps.push(leaf(3, "case 'download-receipt' → download-receipt", 'handler.ts:133-134', result));
      break;

    case 'download-files':
      result = { type: 'download-files-and-submit', task };
      steps.push(leaf(3, "case 'download-files' → download-files-and-submit", 'handler.ts:136-137', result));
      break;

    case 'appointment':
      result = { type: 'open-appointment', task };
      steps.push(leaf(3, "case 'appointment' → open-appointment", 'handler.ts:139-140', result));
      break;

    default:
      result = { type: 'noop' };
      steps.push(leaf(3, `default → noop (actionType '${actionType}' non géré)`, 'handler.ts:142-143', result));
  }

  if (!d && (actionType === 'continue-payment' || actionType === 'pay' || actionType === 'retry-payment')) {
    steps.push({
      index: 4,
      label: 'Demarche non disponible — branche paiement dégradée',
      sourceRef: 'devtools (limitation snapshot)',
      evaluated: 'selectedDemarche()',
      value: null,
      result: 'fail',
      details: ['Le trace réel du handler construit paymentContext depuis la démarche.'],
    });
  }

  return { result, steps };
}

function buildContinuePaymentContext(meta: SyntheticPaymentMeta, d: Demarche): PaymentContextData {
  return {
    submissionId: '',
    amount: meta.paymentAmount,
    currency: meta.paymentCurrency === 'FCFA' ? 'XOF' : meta.paymentCurrency,
    serviceName: d.title || 'Service',
    serviceProvider: meta.paymentGateway,
    requestNumber: d.reference || '',
    tenantId: d.tenantId,
    serviceId: d.serviceUid || d.serviceId,
    serviceUid: d.serviceUid,
    serviceVersionUid: d.serviceVersionUid,
  };
}

function buildPayContext(meta: SyntheticPaymentMeta, d: Demarche): PaymentContextData {
  return {
    submissionId: '',
    amount: meta.paymentAmount,
    currency: meta.paymentCurrency === 'FCFA' ? 'XOF' : meta.paymentCurrency,
    serviceName: d.title || 'Service',
    serviceProvider: '',
    requestNumber: d.reference || '',
    tenantId: d.tenantId,
    serviceId: d.serviceUid || d.serviceId,
    serviceUid: d.serviceUid,
    serviceVersionUid: d.serviceVersionUid,
  };
}

function leaf(
  index: number,
  label: string,
  sourceRef: string,
  result: TaskActionResult,
  details?: string[]
): DecisionStep {
  return {
    index,
    label,
    sourceRef,
    evaluated: 'switch branch',
    value: { type: result.type },
    result: 'pass',
    branchTaken: `→ TaskActionResult.type = '${result.type}'`,
    details,
  };
}

// ============================================================================
// PIPELINE 3 — validateTaskAction tracé (mirror fidèle)
// ============================================================================

function tracePipeline3(
  result: TaskActionResult | null,
  demarche: Demarche | null,
  payments: Payment[]
): { validation: { valid: boolean; reason?: string }; steps: DecisionStep[] } {
  const steps: DecisionStep[] = [];

  if (!result) {
    steps.push({
      index: 1,
      label: 'TaskActionResult disponible ?',
      sourceRef: 'handler.ts:147',
      evaluated: 'P2 output',
      value: null,
      result: 'fail',
      branchTaken: '→ rien à valider',
    });
    return { validation: { valid: false, reason: 'Aucune action à valider (P2 vide)' }, steps };
  }

  steps.push({
    index: 1,
    label: `switch(result.type) — branche '${result.type}'`,
    sourceRef: 'handler.ts:153',
    evaluated: 'result.type',
    value: result.type,
    result: 'pass',
  });

  switch (result.type) {
    case 'view-form': {
      const serviceId = demarche?.serviceId;
      const serviceUid = demarche?.serviceUid;
      const pass = !!(serviceId || serviceUid);
      steps.push({
        index: 2,
        label: 'Check serviceId || serviceUid',
        sourceRef: 'handler.ts:154-158',
        evaluated: '!demarche.serviceId && !demarche.serviceUid',
        value: { serviceId, serviceUid },
        result: pass ? 'pass' : 'fail',
        branchTaken: pass ? '→ valid' : '→ invalid: SERVICE_MANQUANT',
      });
      return {
        validation: pass ? { valid: true } : { valid: false, reason: 'TASK_VALIDATION.SERVICE_MANQUANT' },
        steps,
      };
    }

    case 'download-receipt': {
      const hasSucceededPayment = payments.some(p => p.statusColor === 'success');
      const synthetic = result.task.data?.['__syntheticPayment'] as SyntheticPaymentMeta | undefined;
      const enrichment = result.task.data?.['__paymentEnrichment'] as SyntheticPaymentMeta | undefined;
      const isReceiptAvailable = !!(synthetic?.receiptAvailable || enrichment?.receiptAvailable);
      const pass = hasSucceededPayment || isReceiptAvailable;
      steps.push({
        index: 2,
        label: 'Check hasSucceededPayment || receiptAvailable',
        sourceRef: 'handler.ts:160-168',
        evaluated: "payments.some(p=>p.statusColor==='success') || meta.receiptAvailable",
        value: { hasSucceededPayment, isReceiptAvailable, paymentsCount: payments.length },
        result: pass ? 'pass' : 'fail',
        branchTaken: pass ? '→ valid' : '→ invalid: SERVICE_MOMENTANEMENT_INDISPONIBLE',
      });
      return {
        validation: pass ? { valid: true } : { valid: false, reason: 'TASK_VALIDATION.SERVICE_MOMENTANEMENT_INDISPONIBLE' },
        steps,
      };
    }

    case 'navigate-payment-context': {
      const hasServiceId = !!result.serviceId;
      steps.push({
        index: 2,
        label: 'Check result.serviceId',
        sourceRef: 'handler.ts:171-174',
        evaluated: '!result.serviceId',
        value: result.serviceId || '(vide)',
        result: hasServiceId ? 'pass' : 'fail',
        branchTaken: hasServiceId ? '→ continue' : '→ invalid: SERVICE_MANQUANT',
      });
      if (!hasServiceId) {
        return { validation: { valid: false, reason: 'TASK_VALIDATION.SERVICE_MANQUANT' }, steps };
      }
      const amount = result.paymentContext.amount;
      const validAmount = !!amount && amount > 0;
      steps.push({
        index: 3,
        label: 'Check paymentContext.amount > 0',
        sourceRef: 'handler.ts:175-177',
        evaluated: '!result.paymentContext.amount || amount <= 0',
        value: { amount },
        result: validAmount ? 'pass' : 'fail',
        branchTaken: validAmount ? '→ valid' : '→ invalid: MONTANT_INVALIDE',
      });
      return {
        validation: validAmount ? { valid: true } : { valid: false, reason: 'TASK_VALIDATION.MONTANT_INVALIDE' },
        steps,
      };
    }

    case 'proceed-task':
    case 'proceed-form-view':
    case 'proceed-parent-task': {
      const task =
        'task' in result
          ? result.task
          : 'parentTask' in result
          ? result.parentTask
          : null;
      const status = task?.status;
      const isTerminal = !!status && (TERMINAL_STATUSES as readonly string[]).includes(status);
      steps.push({
        index: 2,
        label: 'Check task.status non terminal',
        sourceRef: 'handler.ts:180-190',
        evaluated: `[${TERMINAL_STATUSES.join(',')}].includes(task.status)`,
        value: { status, isTerminal },
        result: isTerminal ? 'fail' : 'pass',
        branchTaken: isTerminal ? '→ invalid: TACHE_NON_ACTIONNABLE' : '→ valid',
      });
      return {
        validation: isTerminal
          ? { valid: false, reason: 'TASK_VALIDATION.TACHE_NON_ACTIONNABLE' }
          : { valid: true },
        steps,
      };
    }

    default:
      steps.push({
        index: 2,
        label: 'default → valid (pas de check additionnel)',
        sourceRef: 'handler.ts:193-194',
        evaluated: 'switch default',
        value: result.type,
        result: 'pass',
        branchTaken: '→ valid',
      });
      return { validation: { valid: true }, steps };
  }
}

// ============================================================================
// PIPELINE 4 — Dispatch + side effects (incluant soumission silencieuse)
// ============================================================================

interface Pipeline4Output {
  steps: DecisionStep[];
  terminalBehavior: string;
  isSilentSubmission: boolean;
  isInteractiveRedirect: boolean;
  status: SectionStatus;
}

function tracePipeline4(
  result: TaskActionResult | null,
  validation: { valid: boolean; reason?: string },
  demarche: Demarche | null
): Pipeline4Output {
  const steps: DecisionStep[] = [];
  if (!result) {
    steps.push({
      index: 1,
      label: 'Dispatch impossible',
      sourceRef: 'demarches-page.component.ts:629',
      evaluated: 'action',
      value: null,
      result: 'fail',
    });
    return { steps, terminalBehavior: '—', isSilentSubmission: false, isInteractiveRedirect: false, status: 'noop' };
  }

  if (!validation.valid) {
    steps.push({
      index: 1,
      label: 'Dispatch bloqué par validation',
      sourceRef: 'demarches-page.component.ts:624-627',
      evaluated: 'validation.valid',
      value: validation,
      result: 'fail',
      branchTaken: `→ alertService.error('${validation.reason}') ; return`,
      details: ['Aucun side effect n\'est exécuté'],
    });
    return {
      steps,
      terminalBehavior: `BLOQUÉ — ${validation.reason ?? 'invalid'}`,
      isSilentSubmission: false,
      isInteractiveRedirect: false,
      status: 'blocked',
    };
  }

  steps.push({
    index: 1,
    label: `switch(action.type) — branche '${result.type}'`,
    sourceRef: 'demarches-page.component.ts:630',
    evaluated: 'action.type',
    value: result.type,
    result: 'pass',
  });

  let terminal = '';
  let silent = false;
  let interactive = false;
  let status: SectionStatus = 'ok';

  switch (result.type) {
    case 'view-form':
      steps.push(fxStep(2, "formService.loadFormForView(demarche, task)", 'component.ts:631-633', [
        'Ouvre la modal preview du formulaire (lecture seule)',
        'Aucune soumission API',
      ]));
      terminal = 'Modal formulaire (lecture seule)';
      break;

    case 'view-invoice':
      steps.push(fxStep(2, `paymentService.downloadPaymentReceipt('${result.transactionId}')`, 'component.ts:635-637', [
        'HTTP GET /payments/{id}/receipt',
        'Téléchargement blob (PDF)',
      ]));
      terminal = 'Téléchargement reçu (transaction)';
      break;

    case 'download-receipt':
      steps.push(fxStep(2, 'paymentService.downloadTaskReceipt(task)', 'component.ts:639-641', [
        'HTTP GET /payments/{id}/receipt (via task)',
        'Téléchargement blob (PDF)',
      ]));
      terminal = 'Téléchargement reçu (task)';
      break;

    case 'navigate-payment':
      steps.push(fxStep(2, 'navigateToPayment()', 'component.ts:643-645', [
        'router.navigate vers l\'onglet Paiements de la démarche',
      ]));
      terminal = 'Redirect onglet Paiements';
      break;

    case 'view-payment-status':
      steps.push(fxStep(2, 'viewPaymentStatus(task)', 'component.ts:647-649', [
        'Ouvre la modal de statut de paiement',
      ]));
      terminal = 'Modal statut paiement';
      break;

    case 'navigate-payment-context': {
      const ctx = result.paymentContext;
      steps.push(fxStep(2, 'Construction paymentLink', 'component.ts:655-665', [
        `paymentLink = origin + /guest-payment?amount=${ctx.amount}&currency=${ctx.currency}&...`,
        `retryContext = ${result.retryPaymentId ? `{ paymentId: '${result.retryPaymentId}', recordId: ... }` : 'null'}`,
      ]));
      steps.push(fxStep(3, `router.navigate(['/panel/request-form', '${result.serviceId}'])`, 'component.ts:679-688', [
        'state.paymentContext = enrichedContext',
        'state.showPaymentStep = true',
        result.recordId ? `queryParams.recordId = '${result.recordId}'` : 'pas de recordId',
        result.retryPaymentId ? 'state.retryContext = {...}' : 'pas de retryContext',
      ]));
      terminal = `Redirect page paiement (autonome) — serviceId=${result.serviceId}`;
      break;
    }

    case 'proceed-parent-task': {
      silent = true;
      status = 'silent';
      steps.push(fxStep(2, `handleProceedTask(parentTask='${result.parentTask._id}', demarche)`, 'component.ts:691-693', [
        `parentTask.status = '${result.parentTask.status}'`,
        `resolvedHandlerType = '${result.parentTask.handlerType ?? result.parentTask.data?.['handlerType'] ?? '(aucun)'}'`,
      ]));
      pushSilentSubmitSteps(steps, result.parentTask, demarche);
      terminal = 'SOUMISSION SILENCIEUSE (parent) → submitFormView API call';
      break;
    }

    case 'proceed-form-view': {
      interactive = true;
      status = 'interactive';
      const task = result.task;
      const formSetting = (task as unknown as {
        parameters?: { gnspdFormSetting?: { correctionKeys?: unknown[]; message?: { reason?: { correctionKeys?: unknown[] } } } };
        formSetting?: { correctionKeys?: unknown[] };
      });
      const gnspdFormSetting = formSetting.parameters?.['gnspdFormSetting'] ?? formSetting.formSetting ?? null;
      const correctionKeys =
        (gnspdFormSetting as { correctionKeys?: unknown[] } | null)?.correctionKeys ??
        (gnspdFormSetting as { message?: { reason?: { correctionKeys?: unknown[] } } } | null)?.message?.reason?.correctionKeys ??
        [];
      steps.push(fxStep(2, 'formService.proceedWithFormView(task, demarche)', 'component.ts:695-697', [
        'PAS SILENCIEUX — redirection vers formulaire interactif',
        `hasGnspdFormSetting = ${!!gnspdFormSetting}`,
        `correctionKeys.length = ${Array.isArray(correctionKeys) ? correctionKeys.length : 0}`,
      ]));
      steps.push(fxStep(3, 'HTTP GET formsFacade.getFormView(taskId, tenantId)', 'demarches-form.service.ts:94', [
        'Récupère schéma du formulaire (pas de submit)',
      ]));
      steps.push(fxStep(4, 'decompressFormSchema(schemaSource, formData)', 'demarches-form.service.ts:99', [
        'Extrait formComponents, formSettings, formVersionId',
      ]));
      steps.push(fxStep(5, `router.navigate(['/panel/request-form', serviceId])`, 'demarches-form.service.ts:116-132', [
        'state.formComponents, state.formSettings, state.formVersionId',
        'state.initialSubmissionData = task.initialSubmissionData || task.data',
        'state.gnspdFormSetting (mode correction si correctionKeys.length > 0)',
        'state.taskSubmission = { taskId, tenantId, xref, serviceId, serviceUid, applicant }',
      ]));
      terminal = 'REDIRECT INTERACTIF → page request-form (mode correction/resoumission)';
      break;
    }

    case 'proceed-task': {
      silent = true;
      status = 'silent';
      steps.push(fxStep(2, `handleProceedTask(task='${result.task._id}', demarche)`, 'component.ts:699-701', [
        `task.status = '${result.task.status}'`,
        `resolvedHandlerType = '${result.task.handlerType ?? result.task.data?.['handlerType'] ?? '(aucun)'}'`,
      ]));
      pushSilentSubmitSteps(steps, result.task, demarche);
      terminal = 'SOUMISSION SILENCIEUSE → submitFormView API call';
      break;
    }

    case 'download-files-and-submit': {
      const task = result.task;
      const needsSubmit = task.status !== 'Completed' && task.status !== 'Success';
      silent = needsSubmit;
      status = needsSubmit ? 'silent' : 'ok';
      steps.push(fxStep(2, 'paymentService.downloadTaskFiles(task)', 'component.ts:703-704', [
        'Téléchargement des pièces jointes',
      ]));
      steps.push({
        index: 3,
        label: 'Check task.status !== Completed/Success',
        sourceRef: 'component.ts:705',
        evaluated: "task.status !== 'Completed' && task.status !== 'Success'",
        value: { status: task.status, needsSubmit },
        result: needsSubmit ? 'pass' : 'fail',
        branchTaken: needsSubmit ? '→ déclenche silentSubmitTask' : '→ pas de soumission',
      });
      if (needsSubmit) {
        steps.push(fxStep(4, 'formService.silentSubmitTask(task, demarche) — SOUMISSION SILENCIEUSE', 'component.ts:706-709', [
          'HTTP GET formsFacade.getFormView(taskId, tenantId)',
          'decompressFormSchema(...)',
          'buildSubmitBody(task, demarche, components, data, version, null)',
          'HTTP POST submitFormView(taskId, tenantId, submitBody)',
          'puis: tasksCache.delete(demarche.reference) + loadTasks(demarche)',
        ]));
        terminal = 'Download + SOUMISSION SILENCIEUSE (submitFormView)';
      } else {
        terminal = 'Download seul (task déjà complétée)';
      }
      break;
    }

    case 'open-appointment':
      steps.push(fxStep(2, 'appointmentTask = task; isAppointmentModalOpen = true', 'component.ts:713-716', [
        'Ouvre la modal de prise de rendez-vous',
      ]));
      terminal = 'Modal appointment';
      break;

    case 'noop':
      steps.push({
        index: 2,
        label: "case 'noop' — aucun side effect",
        sourceRef: 'component.ts:630 (switch)',
        evaluated: 'action.type',
        value: 'noop',
        result: 'skip',
        branchTaken: '→ rien à faire',
      });
      terminal = 'Aucun side effect';
      status = 'noop';
      break;
  }

  return { steps, terminalBehavior: terminal, isSilentSubmission: silent, isInteractiveRedirect: interactive, status };
}

function pushSilentSubmitSteps(steps: DecisionStep[], task: TaskRecordDto, demarche: Demarche | null): void {
  const baseIdx = steps.length + 1;
  steps.push(fxStep(baseIdx, 'formService.proceedWithTask(task, demarche) — SOUMISSION SILENCIEUSE', 'demarches-form.service.ts:143', [
    'Pipeline interne : getFormView → decompress → buildBody → submitFormView → check requiresPayment',
  ]));
  steps.push(fxStep(baseIdx + 1, 'HTTP GET formsFacade.getFormView(taskId, tenantId)', 'demarches-form.service.ts:157', [
    `taskId = ${task.taskId || task._id}`,
    `tenantId = ${demarche?.tenantId ?? '(snapshot indisponible)'}`,
    'Récupère le schéma du formulaire',
  ]));
  steps.push(fxStep(baseIdx + 2, 'decompressFormSchema(schemaSource, formData)', 'demarches-form.service.ts:162', [
    'Extrait formComponents, formSettings, formVersionId',
  ]));
  steps.push(fxStep(baseIdx + 3, 'buildSubmitBody(task, demarche, components, data, version, settings)', 'demarches-form.service.ts:164', [
    'Construit payload FormSubmissionService',
    'Attache serviceId, peerId, xref, tenantId, workspaceId, applicant',
  ]));
  steps.push(fxStep(baseIdx + 4, 'HTTP POST formsFacade.submitFormView(taskId, tenantId, submitBody) — SOUMISSION', 'demarches-form.service.ts:167', [
    'APPEL RÉSEAU EFFECTIF — le backend enregistre la submission',
    'Retourne submissionResponse { requiresPayment?, calculatedCosts?, xref?, submissionId? }',
  ]));
  const handlerType = task.handlerType || (task.data?.['handlerType'] as string | undefined);
  const isDownloadHandler = handlerType === 'download_file' || handlerType === 'download_files';
  steps.push({
    index: baseIdx + 5,
    label: "Check handlerType ∈ { download_file, download_files } ?",
    sourceRef: 'demarches-form.service.ts:171-173',
    evaluated: "resolvedHandlerType === 'download_file' || 'download_files'",
    value: { resolvedHandlerType: handlerType ?? '(aucun)', isDownloadHandler },
    result: isDownloadHandler ? 'pass' : 'fail',
    branchTaken: isDownloadHandler ? '→ return null — download géré par le caller' : '→ continue',
  });
  steps.push(fxStep(baseIdx + 6, 'Check submissionResponse.requiresPayment', 'demarches-form.service.ts:175', [
    'OUI → construit paymentContext { amount, currency=XOF, requestNumber, tenantId, serviceId, serviceUid } + return { requiresPayment:true, paymentContext }',
    '  → component.handleProceedTask() redirige /panel/request-form/{serviceId} avec state.paymentContext + state.showPaymentStep',
    'NON → return null, component recharge tasks via loadTasks(demarche)',
  ]));
}

function fxStep(index: number, label: string, sourceRef: string, details: string[]): DecisionStep {
  return {
    index,
    label,
    sourceRef,
    evaluated: 'side effect',
    value: '(runtime)',
    result: 'pass',
    details,
  };
}

// ============================================================================
// Orchestration
// ============================================================================

function buildTrace(task: TaskRecordDto, demarche: Demarche | null, tasks: TaskRecordDto[], payments: Payment[]): PipelineTrace {
  const p1 = tracePipeline1(task, payments);
  const p2 = tracePipeline2(task, p1.action, demarche, tasks);
  const p3 = tracePipeline3(p2.result, demarche, payments);
  const p4 = tracePipeline4(p2.result, p3.validation, demarche);

  const sections: PipelineSection[] = [
    {
      id: 'p1',
      title: 'Pipeline 1 — getTaskAction',
      subtitle: 'action "soft" (label + type)',
      status: p1.action ? 'ok' : 'blocked',
      steps: p1.steps,
      summary: p1.action
        ? `type='${p1.action.type}' label='${p1.action.label}'${p1.action.disabled ? ' [disabled]' : ''}`
        : 'Aucune action résolue',
    },
    {
      id: 'p2',
      title: 'Pipeline 2 — resolveTaskAction',
      subtitle: 'dispatch soft → TaskActionResult concret',
      status: p2.result ? (p2.result.type === 'noop' ? 'noop' : 'ok') : 'blocked',
      steps: p2.steps,
      summary: p2.result
        ? `TaskActionResult.type = '${p2.result.type}'`
        : 'Aucune action à résoudre',
    },
    {
      id: 'p3',
      title: 'Pipeline 3 — validateTaskAction',
      subtitle: 'verdict (serviceId, montant, terminal status, reçu)',
      status: p3.validation.valid ? 'ok' : 'blocked',
      steps: p3.steps,
      summary: p3.validation.valid ? '✓ VALID' : `✗ INVALID — ${p3.validation.reason}`,
    },
    {
      id: 'p4',
      title: 'Pipeline 4 — Dispatch & side effects',
      subtitle: p4.isSilentSubmission
        ? '⚠ SOUMISSION SILENCIEUSE (submitFormView)'
        : p4.isInteractiveRedirect
        ? 'redirect formulaire interactif'
        : 'side effects UI/HTTP',
      status: p4.status,
      steps: p4.steps,
      summary: p4.terminalBehavior,
    },
  ];

  const highlights = extractHighlightInfo(task, p2.result?.type ?? null);

  return {
    taskName: task.name || task.label || task._id,
    taskId: task._id,
    status: task.status,
    handlerType: task.handlerType || (task.data?.['handlerType'] as string | undefined) || '—',
    bpmnType: task.bpmnType || '—',
    softAction: p1.action,
    actionResult: p2.result,
    validation: p3.validation,
    terminalBehavior: p4.terminalBehavior,
    isSilentSubmission: p4.isSilentSubmission,
    isInteractiveRedirect: p4.isInteractiveRedirect,
    highlights,
    sections,
  };
}

// ============================================================================
// Composant Angular
// ============================================================================

@Component({
  selector: 'app-dev-tools-state-machine-tab',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="tw-flex tw-flex-col tw-h-full tw-text-gray-200 tw-text-[12px] tw-font-mono">
      <!-- ── Toolbar ─────────────────────────────────────────────────── -->
      <div class="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-border-b tw-border-gray-700 tw-bg-gray-900/60">
        <i class="ph ph-graph tw-text-success-500"></i>
        <span class="tw-font-semibold tw-text-gray-100">State Machine Debugger</span>
        <span class="tw-text-gray-500">—</span>
        <span class="tw-text-gray-400">4 pipelines chaînés</span>
        <div class="tw-ml-auto tw-flex tw-items-center tw-gap-2">
          <button
            type="button"
            class="tw-px-2 tw-py-1 tw-rounded tw-bg-gray-800 hover:tw-bg-gray-700 tw-text-gray-200 tw-border tw-border-gray-700"
            (click)="rerun()"
            title="Re-run trace"
          >
            <i class="ph ph-arrow-clockwise"></i> Re-run
          </button>
          <button
            type="button"
            class="tw-px-2 tw-py-1 tw-rounded tw-bg-gray-800 hover:tw-bg-gray-700 tw-text-gray-200 tw-border tw-border-gray-700"
            [disabled]="!trace()"
            (click)="copyTrace()"
            title="Copier la trace"
          >
            <i class="ph ph-copy"></i> Copy JSON
          </button>
          <span *ngIf="copied()" class="tw-text-success-500 tw-text-[11px]">copié !</span>
        </div>
      </div>

      <!-- ── Filtres ─────────────────────────────────────────────────── -->
      <div class="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-border-b tw-border-gray-800 tw-bg-gray-900/40">
        <label class="tw-text-gray-400">Démarche :</label>
        <select
          class="tw-bg-gray-800 tw-border tw-border-gray-700 tw-rounded tw-px-2 tw-py-1 tw-text-gray-100"
          [value]="selectedDemarcheId() || ''"
          (change)="onDemarcheChange($any($event.target).value)"
        >
          <option value="" disabled>— choisir —</option>
          <option *ngFor="let d of demarches(); trackBy: trackDemarche" [value]="id(d)">
            {{ label(d) }}
          </option>
        </select>

        <label class="tw-text-gray-400 tw-ml-3">handlerType :</label>
        <select
          class="tw-bg-gray-800 tw-border tw-border-gray-700 tw-rounded tw-px-2 tw-py-1 tw-text-gray-100"
          [value]="handlerFilter()"
          (change)="handlerFilter.set($any($event.target).value)"
        >
          <option value="all">all</option>
          <option *ngFor="let h of availableHandlers()" [value]="h">{{ h }}</option>
        </select>

        <span class="tw-ml-auto tw-text-gray-500">
          {{ filteredTasks().length }} / {{ tasks().length }} tasks
        </span>
      </div>

      <!-- ── Diagramme ASCII ──────────────────────────────────────────── -->
      <details class="tw-px-3 tw-py-2 tw-border-b tw-border-gray-800 tw-bg-gray-900/20">
        <summary class="tw-cursor-pointer tw-text-gray-300 hover:tw-text-success-500">
          <i class="ph ph-tree-structure"></i> Pipeline (vue d'ensemble)
        </summary>
        <pre class="tw-mt-2 tw-text-[11px] tw-text-gray-400 tw-leading-tight tw-whitespace-pre">{{ asciiPipeline }}</pre>
      </details>

      <!-- ── Body : tasks + trace ─────────────────────────────────────── -->
      <div class="tw-flex-1 tw-flex tw-min-h-0">
        <!-- Liste tasks -->
        <div class="tw-w-[260px] tw-border-r tw-border-gray-800 tw-overflow-auto">
          <div
            *ngFor="let t of filteredTasks(); trackBy: trackTask"
            class="tw-p-2 tw-border-b tw-border-gray-800 tw-cursor-pointer hover:tw-bg-gray-800/40"
            [ngClass]="t._id === selectedTaskId() ? 'tw-bg-success-500/10 tw-border-l-2 tw-border-l-success-500' : ''"
            (click)="selectTask(t._id)"
          >
            <div class="tw-text-gray-100 tw-truncate" [title]="t.name || t._id">
              {{ t.name || t.label || t._id }}
            </div>
            <div class="tw-flex tw-items-center tw-gap-2 tw-mt-1">
              <span class="tw-px-1.5 tw-py-0.5 tw-rounded tw-text-[10px]" [ngClass]="statusClass(t.status)">
                {{ t.status }}
              </span>
              <span class="tw-text-gray-500 tw-text-[10px]">
                {{ t.handlerType || '—' }}
              </span>
            </div>
          </div>
          <div *ngIf="filteredTasks().length === 0" class="tw-p-4 tw-text-gray-500 tw-text-center">
            Aucune task.
          </div>
        </div>

        <!-- Trace -->
        <div class="tw-flex-1 tw-overflow-auto tw-p-3" *ngIf="trace() as tr; else noTrace">
          <div class="tw-border tw-border-gray-700 tw-rounded tw-bg-gray-900/50">

            <!-- Header task -->
            <div class="tw-px-3 tw-py-2 tw-border-b tw-border-gray-700 tw-flex tw-items-start tw-gap-3">
              <div class="tw-flex-1 tw-min-w-0">
                <div class="tw-text-gray-100 tw-font-semibold tw-truncate">TASK: "{{ tr.taskName }}"</div>
                <div class="tw-text-gray-400 tw-text-[11px] tw-mt-0.5">
                  status: <span [ngClass]="statusClass(tr.status)" class="tw-px-1 tw-rounded">{{ tr.status }}</span>
                  &nbsp;•&nbsp; handlerType: <span class="tw-text-success-500">{{ tr.handlerType }}</span>
                  &nbsp;•&nbsp; bpmnType: <span class="tw-text-info-500">{{ tr.bpmnType }}</span>
                </div>
              </div>
              <!-- Badges -->
              <div class="tw-flex tw-flex-col tw-items-end tw-gap-1">
                <span *ngIf="tr.isSilentSubmission"
                  class="tw-px-2 tw-py-0.5 tw-rounded tw-text-[10px] tw-font-bold tw-bg-error-500 tw-text-gray-900 tw-tracking-wide tw-animate-pulse">
                  ⚠ SILENT SUBMISSION
                </span>
                <span *ngIf="tr.isInteractiveRedirect"
                  class="tw-px-2 tw-py-0.5 tw-rounded tw-text-[10px] tw-font-bold tw-bg-info-500/20 tw-text-info-500 tw-border tw-border-info-500">
                  ↗ REDIRECT INTERACTIF
                </span>
                <span *ngIf="!tr.validation.valid"
                  class="tw-px-2 tw-py-0.5 tw-rounded tw-text-[10px] tw-font-bold tw-bg-error-500/20 tw-text-error-500 tw-border tw-border-error-500">
                  ✗ BLOCKED
                </span>
                <span *ngIf="tr.highlights.configs.length > 0"
                  class="tw-px-2 tw-py-0.5 tw-rounded tw-text-[10px] tw-font-bold tw-bg-alert-500/20 tw-text-alert-500 tw-border tw-border-alert-500">
                  🎯 {{ tr.highlights.configs.length }} CHAMPS HIGHLIGHTÉS
                </span>
              </div>
            </div>

            <!-- ── ACTION FINALE (récap 4 pipelines) ─────────────────── -->
            <div class="tw-px-3 tw-py-2 tw-border-b tw-border-gray-700 tw-bg-gray-900/70">
              <div class="tw-text-gray-300 tw-font-semibold tw-mb-2">→ ACTION FINALE (récap 4 pipelines)</div>

              <!-- P1 -->
              <div class="tw-flex tw-items-start tw-gap-2 tw-text-[11px] tw-mb-1">
                <span class="tw-text-gray-500 tw-w-8">P1</span>
                <ng-container *ngIf="tr.softAction; else p1null">
                  <i [class]="tr.softAction.icon + ' tw-text-success-500'"></i>
                  <span class="tw-text-success-500">{{ tr.softAction.label }}</span>
                  <span class="tw-text-gray-500">(type: <code class="tw-text-warning-500">{{ tr.softAction.type }}</code>)</span>
                  <span *ngIf="tr.softAction.disabled" class="tw-text-error-500 tw-text-[10px]">[disabled]</span>
                </ng-container>
                <ng-template #p1null><span class="tw-text-error-500">null</span></ng-template>
              </div>

              <!-- P2 -->
              <div class="tw-flex tw-items-start tw-gap-2 tw-text-[11px] tw-mb-1">
                <span class="tw-text-gray-500 tw-w-8">P2</span>
                <ng-container *ngIf="tr.actionResult; else p2null">
                  <span class="tw-text-info-500">TaskActionResult</span>
                  <span class="tw-text-gray-500">type =</span>
                  <code class="tw-text-warning-500">'{{ tr.actionResult.type }}'</code>
                  <span *ngIf="actionResultExtras(tr.actionResult)" class="tw-text-gray-500">
                    — {{ actionResultExtras(tr.actionResult) }}
                  </span>
                </ng-container>
                <ng-template #p2null><span class="tw-text-error-500">null</span></ng-template>
              </div>

              <!-- P3 -->
              <div class="tw-flex tw-items-start tw-gap-2 tw-text-[11px] tw-mb-1">
                <span class="tw-text-gray-500 tw-w-8">P3</span>
                <span [ngClass]="tr.validation.valid ? 'tw-text-success-500' : 'tw-text-error-500'">
                  {{ tr.validation.valid ? '✓ VALID' : '✗ INVALID' }}
                </span>
                <span *ngIf="tr.validation.reason" class="tw-text-error-500">— {{ tr.validation.reason }}</span>
              </div>

              <!-- P4 -->
              <div class="tw-flex tw-items-start tw-gap-2 tw-text-[11px]">
                <span class="tw-text-gray-500 tw-w-8">P4</span>
                <span [ngClass]="terminalColor(tr)">
                  {{ tr.terminalBehavior }}
                </span>
              </div>
            </div>

            <!-- ── 📌 CHAMPS À CORRIGER (highlights) ─────────────────── -->
            <div *ngIf="tr.highlights.isResubmissionContext"
              class="tw-px-3 tw-py-2 tw-border-b tw-border-gray-700 tw-bg-gray-900/40">
              <div class="tw-flex tw-items-center tw-gap-2 tw-mb-2">
                <span class="tw-text-gray-100 tw-font-semibold">📌 CHAMPS À CORRIGER (highlights)</span>
                <span *ngIf="tr.highlights.configs.length > 0"
                  class="tw-px-1.5 tw-py-0.5 tw-rounded tw-text-[10px] tw-font-bold"
                  [ngClass]="priorityBadgeClass(maxPriority(tr.highlights.configs))">
                  {{ maxPriority(tr.highlights.configs) | uppercase }}
                </span>
                <span class="tw-text-gray-400 tw-text-[11px]">
                  {{ tr.highlights.configs.length }} champ(s)
                </span>
                <button
                  *ngIf="tr.highlights.configs.length > 0"
                  type="button"
                  class="tw-ml-auto tw-px-2 tw-py-0.5 tw-rounded tw-bg-gray-800 hover:tw-bg-gray-700 tw-text-gray-200 tw-border tw-border-gray-700 tw-text-[11px]"
                  (click)="copyHighlights()"
                  title="Copier les highlightConfigs en JSON"
                >
                  <i class="ph ph-copy"></i> Copier JSON
                </button>
                <span *ngIf="highlightCopied()" class="tw-text-success-500 tw-text-[11px]">copié !</span>
              </div>

              <div class="tw-text-[11px] tw-mb-1">
                <span class="tw-text-gray-500">source: </span>
                <code class="tw-text-warning-500">{{ tr.highlights.source }}</code>
                <span class="tw-text-gray-500"> — extracted from </span>
                <code class="tw-text-info-500">{{ sourcePath(tr.highlights.source) }}</code>
              </div>

              <div *ngIf="tr.highlights.globalMessage" class="tw-text-[11px] tw-italic tw-text-gray-300 tw-mb-2">
                « {{ tr.highlights.globalMessage }} »
              </div>

              <div *ngIf="tr.highlights.willOpenForm && tr.highlights.configs.length === 0"
                class="tw-text-[11px] tw-text-warning-500 tw-bg-warning-500/10 tw-border tw-border-warning-500/40 tw-rounded tw-px-2 tw-py-1 tw-mb-2">
                ⚠ Aucun correctionKey reçu — le formulaire sera ouvert sans surlignage
                (anomalie possible : le backend a rejeté mais n'a pas précisé les champs).
              </div>

              <div *ngIf="tr.highlights.configs.length > 0" class="tw-overflow-x-auto">
                <table class="tw-w-full tw-text-[11px] tw-border-collapse">
                  <thead>
                    <tr class="tw-text-gray-400 tw-border-b tw-border-gray-700">
                      <th class="tw-text-left tw-py-1 tw-pr-2 tw-font-medium">fieldKey</th>
                      <th class="tw-text-left tw-py-1 tw-pr-2 tw-font-medium">type</th>
                      <th class="tw-text-left tw-py-1 tw-pr-2 tw-font-medium">priority</th>
                      <th class="tw-text-left tw-py-1 tw-pr-2 tw-font-medium">autoFocus</th>
                      <th class="tw-text-left tw-py-1 tw-pr-2 tw-font-medium">message</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr *ngFor="let h of tr.highlights.configs; trackBy: trackHighlight"
                      class="tw-border-b tw-border-gray-800 tw-align-top">
                      <td class="tw-py-1 tw-pr-2">
                        <code [ngClass]="highlightColor(h.highlightType)">{{ h.fieldKey }}</code>
                      </td>
                      <td class="tw-py-1 tw-pr-2">
                        <code [ngClass]="highlightColor(h.highlightType)">{{ h.highlightType }}</code>
                      </td>
                      <td class="tw-py-1 tw-pr-2">
                        <span [ngClass]="priorityTextClass(h.priority)">{{ h.priority || '—' }}</span>
                      </td>
                      <td class="tw-py-1 tw-pr-2">
                        <span [ngClass]="h.autoFocus ? 'tw-text-success-500' : 'tw-text-gray-500'">
                          {{ h.autoFocus ? '✓' : '—' }}
                        </span>
                      </td>
                      <td class="tw-py-1 tw-pr-2 tw-text-gray-300">
                        {{ h.message || '—' }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- ── 4 SECTIONS détaillées ─────────────────────────────── -->
            <div class="tw-divide-y tw-divide-gray-800">
              <details
                *ngFor="let section of tr.sections; trackBy: trackSection"
                class="tw-px-3 tw-py-2"
                [open]="section.id === 'p4' || section.status === 'blocked' || section.status === 'silent'"
              >
                <summary class="tw-cursor-pointer tw-flex tw-items-center tw-gap-2">
                  <span class="tw-text-[14px]" [ngClass]="sectionIconClass(section.status)">
                    {{ sectionIcon(section.status) }}
                  </span>
                  <span class="tw-text-gray-100 tw-font-semibold">▶ {{ section.title }}</span>
                  <span class="tw-text-gray-500 tw-text-[10px]">— {{ section.subtitle }}</span>
                  <span class="tw-ml-auto tw-text-[10px]" [ngClass]="sectionBadgeClass(section.status)">
                    {{ sectionBadgeLabel(section.status) }}
                  </span>
                </summary>

                <div class="tw-mt-2 tw-ml-2 tw-pl-2 tw-border-l tw-border-gray-800">
                  <div *ngIf="section.summary" class="tw-mb-2 tw-text-[11px]" [ngClass]="sectionIconClass(section.status)">
                    {{ section.summary }}
                  </div>
                  <details
                    *ngFor="let s of section.steps; trackBy: trackStep"
                    class="tw-py-1"
                    [open]="s.result === 'pass' || s.result === 'fail'"
                  >
                    <summary class="tw-cursor-pointer tw-flex tw-items-start tw-gap-2">
                      <span [ngClass]="markClass(s.result)" class="tw-font-bold tw-w-4 tw-text-center">{{ mark(s.result) }}</span>
                      <span class="tw-text-gray-500">[{{ s.index }}]</span>
                      <span class="tw-text-gray-200">{{ s.label }}</span>
                      <span class="tw-ml-2 tw-text-gray-500 tw-text-[10px]">→ {{ resultLabel(s.result) }}</span>
                    </summary>
                    <div class="tw-mt-1 tw-ml-6 tw-space-y-1 tw-text-[11px]">
                      <div>
                        <span class="tw-text-gray-500">evaluated:</span>
                        <code class="tw-text-warning-500">{{ s.evaluated }}</code>
                      </div>
                      <div>
                        <span class="tw-text-gray-500">value:</span>
                        <code class="tw-text-info-500">{{ fmt(s.value) }}</code>
                      </div>
                      <div *ngIf="s.branchTaken">
                        <span class="tw-text-gray-500">branch:</span>
                        <code class="tw-text-success-500">{{ s.branchTaken }}</code>
                      </div>
                      <ul *ngIf="s.details?.length" class="tw-list-disc tw-ml-5 tw-text-gray-400">
                        <li *ngFor="let d of s.details">{{ d }}</li>
                      </ul>
                      <div class="tw-text-gray-500 tw-text-[10px]">source: {{ s.sourceRef }}</div>
                    </div>
                  </details>
                </div>
              </details>
            </div>
          </div>
        </div>
        <ng-template #noTrace>
          <div class="tw-flex-1 tw-flex tw-items-center tw-justify-center tw-text-gray-500">
            Sélectionne une task à gauche pour voir la decision trace.
          </div>
        </ng-template>
      </div>
    </div>
  `,
})
export class DevToolsStateMachineTabComponent {
  private readonly devTools = inject(DevToolsService);

  protected readonly asciiPipeline = [
    'PIPELINE 1 — getTaskAction(task, payments)        → soft action',
    '│   [1] __syntheticPayment || __paymentEnrichment',
    '│   [2] bpmnType = START_EVENT',
    '│   [3] handlerType ∈ {tarification, generate_bordereau, download_file(s), appointment, publish_submission}',
    '│   [4] formSetting.status=rejected || PendingResubmit',
    '│   [5] legacy fallback (nom/bpmn)',
    '▼',
    'PIPELINE 2 — resolveTaskAction(event, demarche, tasks, payments)',
    '│   switch(actionType) → TaskActionResult concret',
    '│     • view-form | view-invoice | navigate-payment | view-payment-status',
    '│     • navigate-payment-context (construction paymentContext)',
    '│     • proceed-task | proceed-form-view | proceed-parent-task',
    '│     • download-receipt | download-files-and-submit | open-appointment | noop',
    '▼',
    'PIPELINE 3 — validateTaskAction(result, demarche, tasks, payments)',
    '│   • view-form          → serviceId || serviceUid',
    '│   • download-receipt   → hasSucceededPayment || receiptAvailable',
    '│   • navigate-pay-ctx   → serviceId + amount > 0',
    '│   • proceed-*          → status non terminal',
    '▼',
    'PIPELINE 4 — Dispatch onTaskAction + handleProceedTask',
    '    proceed-task / proceed-parent-task / download-files-and-submit',
    '      → proceedWithTask()  ⚠ SOUMISSION SILENCIEUSE',
    '        getFormView → decompress → buildBody → submitFormView',
    '        check requiresPayment → navigate page paiement | reload tasks',
    '    proceed-form-view  → proceedWithFormView()  ↗ INTERACTIF (state.gnspdFormSetting)',
  ].join('\n');

  private readonly tick = signal(0);

  private readonly snap = computed(() => {
    this.tick();
    try {
      return this.devTools.snapshot();
    } catch {
      return null;
    }
  });

  protected readonly demarches = computed<unknown[]>(() => {
    const s = this.snap();
    if (!s) return [];
    const extrasList = (s.extras?.['demarches'] as unknown[] | undefined) ?? undefined;
    if (Array.isArray(extrasList) && extrasList.length > 0) return extrasList;
    return s.demarche ? [s.demarche] : [];
  });

  protected readonly selectedDemarcheId = signal<string | null>(null);

  private readonly defaultDemarcheId = computed(() => {
    const list = this.demarches();
    if (!list.length) return null;
    return this.id(list[0]);
  });

  protected readonly currentDemarche = computed<Demarche | null>(() => {
    const sel = this.selectedDemarcheId() ?? this.defaultDemarcheId();
    const list = this.demarches();
    const found = list.find((d) => this.id(d) === sel) ?? list[0] ?? null;
    return (found as Demarche | null) ?? null;
  });

  protected readonly tasks = computed<TaskRecordDto[]>(() => {
    const s = this.snap();
    if (!s) return [];
    const all = (s.tasks as TaskRecordDto[]) ?? [];
    const d = this.currentDemarche();
    if (!d || this.demarches().length <= 1) return all;
    const ref = d.reference || d.id || '';
    if (!ref) return all;
    return all.filter((t) => t.record === ref || t.xref === ref);
  });

  protected readonly payments = computed<Payment[]>(() => {
    const s = this.snap();
    return ((s?.payments ?? []) as Payment[]);
  });

  protected readonly availableHandlers = computed<string[]>(() => {
    const set = new Set<string>();
    for (const t of this.tasks()) {
      const h = t.handlerType || (t.data?.['handlerType'] as string | undefined);
      if (h) set.add(h);
    }
    return Array.from(set).sort();
  });

  protected readonly handlerFilter = signal<string>('all');

  protected readonly filteredTasks = computed<TaskRecordDto[]>(() => {
    const h = this.handlerFilter();
    const list = this.tasks();
    if (h === 'all') return list;
    return list.filter((t) => (t.handlerType || t.data?.['handlerType']) === h);
  });

  protected readonly selectedTaskId = signal<string | null>(null);

  protected readonly trace = computed<PipelineTrace | null>(() => {
    const id = this.selectedTaskId();
    if (!id) return null;
    const task = this.tasks().find((t) => t._id === id);
    if (!task) return null;
    return buildTrace(task, this.currentDemarche(), this.tasks(), this.payments());
  });

  protected readonly copied = signal(false);
  protected readonly highlightCopied = signal(false);

  // ── Handlers ────────────────────────────────────────────────────────
  selectTask(id: string): void {
    this.selectedTaskId.set(id);
  }

  onDemarcheChange(value: string): void {
    this.selectedDemarcheId.set(value || null);
    this.selectedTaskId.set(null);
  }

  rerun(): void {
    this.tick.update((n) => n + 1);
  }

  copyTrace(): void {
    const tr = this.trace();
    if (!tr) return;
    const json = safeStringify(tr);
    try {
      const nav = typeof navigator !== 'undefined' ? navigator : undefined;
      if (nav?.clipboard?.writeText) {
        void nav.clipboard.writeText(json);
      }
      this.copied.set(true);
      setTimeout(() => this.copied.set(false), 1500);
    } catch {
      /* ignore */
    }
  }

  // ── Helpers template ───────────────────────────────────────────────
  id(d: unknown): string {
    const o = d as { id?: string; uid?: string; reference?: string; xref?: string; _id?: string } | null;
    return o?.id || o?.uid || o?.reference || o?.xref || o?._id || '';
  }

  label(d: unknown): string {
    const o = d as { title?: string; reference?: string; xref?: string; id?: string; status?: string } | null;
    const ref = o?.reference || o?.xref || o?.id || '';
    if (ref) {
      return `${o?.title || 'Démarche'} (${ref})${o?.status ? ` [${o.status}]` : ''}`;
    }
    return this.id(d);
  }

  mark(r: StepResult): string {
    return r === 'pass' ? '✓' : r === 'fail' ? '✗' : '⊘';
  }
  markClass(r: StepResult): string {
    return r === 'pass'
      ? 'tw-text-success-500'
      : r === 'fail'
      ? 'tw-text-error-500'
      : 'tw-text-gray-500';
  }
  resultLabel(r: StepResult): string {
    return r === 'pass' ? 'OUI' : r === 'fail' ? 'NON' : 'skipped';
  }

  sectionIcon(s: SectionStatus): string {
    switch (s) {
      case 'ok': return '✓';
      case 'blocked': return '✗';
      case 'silent': return '⚠';
      case 'interactive': return '↗';
      case 'noop': return '⊘';
    }
  }
  sectionIconClass(s: SectionStatus): string {
    switch (s) {
      case 'ok': return 'tw-text-success-500';
      case 'blocked': return 'tw-text-error-500';
      case 'silent': return 'tw-text-error-500';
      case 'interactive': return 'tw-text-info-500';
      case 'noop': return 'tw-text-gray-500';
    }
  }
  sectionBadgeLabel(s: SectionStatus): string {
    switch (s) {
      case 'ok': return 'OK';
      case 'blocked': return 'BLOCKED';
      case 'silent': return 'SILENT SUBMISSION';
      case 'interactive': return 'INTERACTIVE';
      case 'noop': return 'NOOP';
    }
  }
  sectionBadgeClass(s: SectionStatus): string {
    switch (s) {
      case 'ok': return 'tw-text-success-500 tw-border tw-border-success-500/50 tw-px-1.5 tw-py-0.5 tw-rounded';
      case 'blocked': return 'tw-text-error-500 tw-border tw-border-error-500 tw-px-1.5 tw-py-0.5 tw-rounded tw-font-bold';
      case 'silent': return 'tw-bg-error-500 tw-text-gray-900 tw-px-1.5 tw-py-0.5 tw-rounded tw-font-bold';
      case 'interactive': return 'tw-text-info-500 tw-border tw-border-info-500 tw-px-1.5 tw-py-0.5 tw-rounded';
      case 'noop': return 'tw-text-gray-500 tw-border tw-border-gray-700 tw-px-1.5 tw-py-0.5 tw-rounded';
    }
  }

  terminalColor(tr: PipelineTrace): string {
    if (!tr.validation.valid) return 'tw-text-error-500';
    if (tr.isSilentSubmission) return 'tw-text-error-500 tw-font-bold';
    if (tr.isInteractiveRedirect) return 'tw-text-info-500';
    return 'tw-text-success-500';
  }

  actionResultExtras(r: TaskActionResult): string {
    switch (r.type) {
      case 'view-invoice': return `transactionId='${r.transactionId}'`;
      case 'navigate-payment-context':
        return `serviceId='${r.serviceId}', amount=${r.paymentContext.amount}, currency=${r.paymentContext.currency}${r.retryPaymentId ? `, retryPaymentId='${r.retryPaymentId}'` : ''}`;
      case 'proceed-parent-task': return `parentTask._id='${r.parentTask._id}', status='${r.parentTask.status}'`;
      case 'proceed-task':
      case 'proceed-form-view':
      case 'download-files-and-submit':
      case 'open-appointment':
      case 'download-receipt':
      case 'view-form':
      case 'navigate-payment':
      case 'view-payment-status':
        return `task._id='${r.task._id}'`;
      default: return '';
    }
  }

  fmt(v: unknown): string {
    if (v === undefined) return 'undefined';
    if (v === null) return 'null';
    if (typeof v === 'string') return `'${v}'`;
    if (typeof v === 'number' || typeof v === 'boolean') return String(v);
    try {
      return JSON.stringify(v);
    } catch {
      return String(v);
    }
  }

  statusClass(status: string): string {
    const s = (status || '').toLowerCase();
    if (s === 'completed' || s === 'success' || s === 'validated') return 'tw-bg-success-500/20 tw-text-success-500';
    if (s.startsWith('pending') || s === 'submitted' || s === 'inprogress') return 'tw-bg-warning-500/20 tw-text-warning-500';
    if (s === 'rejected' || s === 'failed' || s === 'reqfail' || s === 'cancelled') return 'tw-bg-error-500/20 tw-text-error-500';
    if (s === 'review') return 'tw-bg-info-500/20 tw-text-info-500';
    return 'tw-bg-gray-800 tw-text-gray-300';
  }

  trackTask = (_: number, t: TaskRecordDto) => t._id;
  trackDemarche = (_: number, d: unknown) => this.id(d);
  trackSection = (_: number, s: PipelineSection) => s.id;
  trackStep = (_: number, s: DecisionStep) => s.index + ':' + s.label;
  trackHighlight = (i: number, h: FieldHighlightConfig) => `${i}:${h.fieldKey}:${h.highlightType}`;

  // ── Highlights helpers ─────────────────────────────────────────────
  copyHighlights(): void {
    const tr = this.trace();
    if (!tr || tr.highlights.configs.length === 0) return;
    const json = safeStringify(tr.highlights.configs);
    try {
      const nav = typeof navigator !== 'undefined' ? navigator : undefined;
      if (nav?.clipboard?.writeText) {
        void nav.clipboard.writeText(json);
      }
      this.highlightCopied.set(true);
      setTimeout(() => this.highlightCopied.set(false), 1500);
    } catch {
      /* ignore */
    }
  }

  sourcePath(s: HighlightSource): string {
    switch (s) {
      case 'direct': return 'task.formSetting.correctionKeys';
      case 'message.reason': return 'task.formSetting.message.reason.correctionKeys';
      case 'aucun': return '(aucune source)';
    }
  }

  maxPriority(configs: FieldHighlightConfig[]): HighlightPriority {
    const order: HighlightPriority[] = ['low', 'medium', 'high', 'critical'];
    let max: HighlightPriority = 'low';
    for (const c of configs) {
      const p = c.priority ?? 'medium';
      if (order.indexOf(p) > order.indexOf(max)) max = p;
    }
    return max;
  }

  priorityBadgeClass(p: HighlightPriority): string {
    switch (p) {
      case 'critical': return 'tw-bg-error-500 tw-text-gray-900';
      case 'high': return 'tw-bg-warning-500 tw-text-gray-900';
      case 'medium': return 'tw-bg-info-500/20 tw-text-info-500 tw-border tw-border-info-500';
      case 'low': return 'tw-bg-gray-700 tw-text-gray-300';
    }
  }

  priorityTextClass(p: HighlightPriority | undefined): string {
    switch (p) {
      case 'critical': return 'tw-text-error-500 tw-font-bold';
      case 'high': return 'tw-text-warning-500 tw-font-bold';
      case 'medium': return 'tw-text-info-500';
      case 'low': return 'tw-text-gray-400';
      default: return 'tw-text-gray-500';
    }
  }

  highlightColor(t: HighlightType): string {
    switch (t) {
      case 'update-required': return 'tw-text-warning-500';
      case 'validation-error': return 'tw-text-error-500';
      case 'warning': return 'tw-text-alert-500';
      case 'info': return 'tw-text-info-500';
      case 'success': return 'tw-text-success-500';
      case 'changed': return 'tw-text-gov-secondary';
      default: return 'tw-text-gray-300';
    }
  }
}
